// ═══════════════════════════════════════════════════════════════
//  Revenue Modelling
//  Driver-based: Price × Volume × Mix × (1 - Discount)
//  Live recalculation, scenario comparison, AG Grid output
// ═══════════════════════════════════════════════════════════════
import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import LoaderComponent from "../../../../libraries/loader/loader";
import { PBExecuteQuery, PBCreateData, Q, userMeta, fmtDate } from "../../../../services/process/PlanningBudgeting/planningBudgetingService";

const COLORS = ["#4969ab","#2d8c4e","#e67e22","#7b68ee","#c0392b","#27ae60","#2980b9","#8e44ad"];

const DEFAULT_DRIVER = {
  product_line: "", region: "", channel: "Direct",
  unit_price: 0, volume: 0, mix_percent: 100, discount_percent: 0,
  budget_year: new Date().getFullYear(), status: "Draft",
};

const calcRevenue = (d) =>
  Number(d.unit_price) * Number(d.volume) * (Number(d.mix_percent) / 100) * (1 - Number(d.discount_percent) / 100);

const CHANNELS   = ["Direct","Online","Partner","Retail","Government","Export"];
const STATUSES   = ["Draft","Submitted","Approved","Rejected"];
const FIELD_STYLE = { padding: "6px 10px", borderRadius: 7, border: "1px solid #d0d8ec", fontSize: 13, width: "100%", boxSizing: "border-box" };
const LABEL_STYLE = { fontSize: 11, color: "#888", marginBottom: 3, display: "block", fontWeight: 600 };

export default function RevenueModelling() {
  const [drivers,  setDrivers]  = useState([]);
  const [driver,   setDriver]   = useState({ ...DEFAULT_DRIVER });
  const [editId,   setEditId]   = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [tab,      setTab]      = useState("model"); // "model" | "view" | "compare"
  const [qf,       setQf]       = useState("");
  const gridRef = useRef(null);

  const [userCredentials] = useState(
    JSON.parse(sessionStorage.getItem("userCredentials") || "{}")
  );

  const fetchDrivers = useCallback(async () => {
    setLoading(true);
    try {
      const c = PBExecuteQuery();
      const res = await axios.post(c[1], { query: Q.revenueDrivers(userCredentials.selectedentity) }, c[0]);
      setDrivers(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [userCredentials.selectedentity]);

  useEffect(() => { fetchDrivers(); }, [fetchDrivers]);

  const liveRev   = calcRevenue(driver);
  const totalRev  = drivers.reduce((s, d) => s + Number(d.calculated_revenue || 0), 0);

  // Channel pie data
  const channelData = Object.entries(
    drivers.reduce((acc, d) => {
      acc[d.channel] = (acc[d.channel] || 0) + Number(d.calculated_revenue || 0);
      return acc;
    }, {})
  ).map(([name, value]) => ({ name, value }));

  // Product bar data
  const productData = Object.entries(
    drivers.reduce((acc, d) => {
      acc[d.product_line] = (acc[d.product_line] || 0) + Number(d.calculated_revenue || 0);
      return acc;
    }, {})
  ).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 10);

  const handleField = (k, v) => setDriver((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    const rev = calcRevenue(driver);
    const payload = {
      database: "planning_budgeting",
      table: "revenue_driver",
      formData: {
        ...driver,
        calculated_revenue: rev,
        ...userMeta(userCredentials),
        ...(editId ? { id: editId, last_updated_on: fmtDate() } : {}),
      },
    };
    try {
      const c = PBCreateData();
      await axios.post(c[1], payload, c[0]);
      alert(editId ? "Driver updated!" : "Driver saved!");
      setDriver({ ...DEFAULT_DRIVER });
      setEditId(null);
      fetchDrivers();
      setTab("view");
    } catch (e) {
      alert("Error saving driver");
    }
  };

  const handleEdit = (row) => {
    setDriver(row);
    setEditId(row.id);
    setTab("model");
  };

  const COL_DEFS = [
    { headerName: "Product Line",  field: "product_line",       sortable: true, filter: true, resizable: true },
    { headerName: "Region",        field: "region",              sortable: true, filter: true },
    { headerName: "Channel",       field: "channel",             sortable: true, filter: true },
    { headerName: "Year",          field: "budget_year",         sortable: true, filter: true, width: 90 },
    { headerName: "Unit Price",    field: "unit_price",          sortable: true, filter: true,
      cellRenderer: (p) => `₹${Number(p.value||0).toLocaleString("en-IN")}` },
    { headerName: "Volume",        field: "volume",              sortable: true, filter: true },
    { headerName: "Mix %",         field: "mix_percent",         sortable: true, filter: true, width: 90 },
    { headerName: "Discount %",    field: "discount_percent",    sortable: true, filter: true, width: 110 },
    { headerName: "Revenue (₹)",   field: "calculated_revenue",  sortable: true, filter: true,
      cellStyle: () => ({ fontWeight: 700, color: "#2d8c4e" }),
      cellRenderer: (p) => `₹${Number(p.value||0).toLocaleString("en-IN")}` },
    { headerName: "Status",        field: "status",              sortable: true, filter: true,
      cellStyle: (p) => ({ color: p.value === "Approved" ? "#2d8c4e" : p.value === "Rejected" ? "#c0392b" : "#888", fontWeight: 500 }) },
    { headerName: "Edit",          field: "edit",                width: 70,
      cellRendererFramework: (p) => (
        <span style={{ cursor: "pointer", color: "#4969ab" }} onClick={() => handleEdit(p.data)}>✏️</span>
      )},
  ];

  if (loading) return <LoaderComponent loaderOpen />;

  return (
    <div style={{ padding: "4px 2px" }}>
      <div style={{ fontSize: 18, fontWeight: 800, color: "#1a2340", marginBottom: 4 }}>Revenue Modelling</div>
      <div style={{ fontSize: 12, color: "#aaa", marginBottom: 18 }}>
        Driver-based revenue model — Price × Volume × Mix × (1 − Discount)
      </div>

      {/* ── Tabs ── */}
      <div style={{ display: "flex", gap: 0, marginBottom: 20, borderBottom: "2px solid #e8ecf4" }}>
        {[["model","🧮 Revenue Model"],["view","📋 All Drivers"],["compare","📊 Analysis"]].map(([k, lbl]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            padding: "8px 20px", border: "none", cursor: "pointer",
            background: tab === k ? "#4969ab" : "transparent",
            color: tab === k ? "#fff" : "#666",
            fontWeight: tab === k ? 700 : 400,
            fontSize: 13, borderRadius: "8px 8px 0 0",
            borderBottom: tab === k ? "2px solid #4969ab" : "none",
            transition: "all 0.15s",
          }}>{lbl}</button>
        ))}
      </div>

      {/* ══ TAB: Model ══ */}
      {tab === "model" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>

          {/* Input form */}
          <div style={{
            background: "#fff", borderRadius: 14,
            boxShadow: "0 2px 14px rgba(73,105,171,0.09)", padding: "22px 24px",
          }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#1a2340", marginBottom: 18 }}>
              {editId ? "✏️ Edit Driver" : "➕ Add Revenue Driver"}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              {[
                { key: "product_line", label: "Product / Service Line", type: "text" },
                { key: "region",       label: "Region",                 type: "text" },
                { key: "budget_year",  label: "Budget Year",            type: "number" },
              ].map(({ key, label, type }) => (
                <div key={key} style={{ gridColumn: key === "product_line" ? "span 2" : "auto" }}>
                  <label style={LABEL_STYLE}>{label}</label>
                  <input type={type} value={driver[key]} onChange={(e) => handleField(key, e.target.value)} style={FIELD_STYLE} />
                </div>
              ))}

              <div>
                <label style={LABEL_STYLE}>Sales Channel</label>
                <select value={driver.channel} onChange={(e) => handleField("channel", e.target.value)} style={FIELD_STYLE}>
                  {CHANNELS.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>

              <div>
                <label style={LABEL_STYLE}>Status</label>
                <select value={driver.status} onChange={(e) => handleField("status", e.target.value)} style={FIELD_STYLE}>
                  {STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>

              {/* Numeric drivers with live sliders */}
              {[
                { key: "unit_price",       label: "Unit Price (₹)",  min: 0,   max: 1000000, step: 100 },
                { key: "volume",           label: "Volume (Units)",   min: 0,   max: 1000000, step: 10  },
                { key: "mix_percent",      label: "Mix %",            min: 0,   max: 100,     step: 1   },
                { key: "discount_percent", label: "Discount %",       min: 0,   max: 100,     step: 0.5 },
              ].map(({ key, label, min, max, step }) => (
                <div key={key} style={{ gridColumn: "span 2" }}>
                  <label style={LABEL_STYLE}>
                    {label}
                    <span style={{ float: "right", color: "#4969ab", fontWeight: 800 }}>
                      {key === "unit_price" ? `₹${Number(driver[key]).toLocaleString("en-IN")}` : `${driver[key]}${key.includes("percent") ? "%" : ""}`}
                    </span>
                  </label>
                  <input
                    type="range" min={min} max={max} step={step}
                    value={driver[key]}
                    onChange={(e) => handleField(key, Number(e.target.value))}
                    style={{ width: "100%", accentColor: "#4969ab" }}
                  />
                  <input
                    type="number" value={driver[key]}
                    onChange={(e) => handleField(key, Number(e.target.value))}
                    style={{ ...FIELD_STYLE, marginTop: 4, width: "50%" }}
                  />
                </div>
              ))}
            </div>

            <div style={{ marginTop: 18, display: "flex", gap: 10 }}>
              <button onClick={handleSave} style={{
                padding: "9px 24px", background: "#4969ab", color: "#fff",
                border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer", fontSize: 14,
              }}>{editId ? "Update Driver" : "Save Driver"}</button>
              {editId && (
                <button onClick={() => { setDriver({ ...DEFAULT_DRIVER }); setEditId(null); }} style={{
                  padding: "9px 20px", background: "#888", color: "#fff",
                  border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer", fontSize: 13,
                }}>Cancel</button>
              )}
            </div>
          </div>

          {/* Live calculation preview */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Live result card */}
            <div style={{
              background: "linear-gradient(135deg, #4969ab, #7b68ee)",
              borderRadius: 14, padding: "28px 24px", color: "#fff",
            }}>
              <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.8 }}>
                Calculated Revenue
              </div>
              <div style={{ fontSize: 36, fontWeight: 900, letterSpacing: -1 }}>
                ₹{liveRev.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
              </div>
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
                = ₹{Number(driver.unit_price).toLocaleString("en-IN")} × {Number(driver.volume).toLocaleString("en-IN")} units × {driver.mix_percent}% mix × (1 − {driver.discount_percent}% disc)
              </div>
              <div style={{ marginTop: 18, paddingTop: 16, borderTop: "1px solid rgba(255,255,255,0.2)" }}>
                <div style={{ fontSize: 11, opacity: 0.65, marginBottom: 8 }}>Total Portfolio Revenue</div>
                <div style={{ fontSize: 22, fontWeight: 800 }}>₹{totalRev.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</div>
                <div style={{ fontSize: 10, opacity: 0.6, marginTop: 2 }}>{drivers.length} active drivers</div>
              </div>
            </div>

            {/* Driver breakdown tiles */}
            {[
              { label: "Gross Revenue",    value: Number(driver.unit_price) * Number(driver.volume), color: "#4969ab" },
              { label: "Mix Adjustment",   value: Number(driver.unit_price) * Number(driver.volume) * (Number(driver.mix_percent)/100), color: "#7b68ee" },
              { label: "Discount Amount",  value: Number(driver.unit_price) * Number(driver.volume) * (Number(driver.mix_percent)/100) * (Number(driver.discount_percent)/100), color: "#c0392b" },
              { label: "Net Revenue",      value: liveRev, color: "#2d8c4e" },
            ].map(({ label, value, color }) => (
              <div key={label} style={{
                background: "#fff", borderRadius: 10, padding: "12px 16px",
                boxShadow: "0 2px 8px rgba(73,105,171,0.08)",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                borderLeft: `3px solid ${color}`,
              }}>
                <span style={{ fontSize: 12, color: "#888" }}>{label}</span>
                <span style={{ fontSize: 15, fontWeight: 800, color }}>
                  ₹{value.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ══ TAB: View ══ */}
      {tab === "view" && (
        <div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
            <input type="text" placeholder="Search drivers..."
              value={qf} onChange={(e) => { setQf(e.target.value); gridRef.current?.api?.setQuickFilter(e.target.value); }}
              style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #d0d8ec", fontSize: 12, width: 260 }} />
          </div>
          <div className="ag-theme-alpine" style={{ height: 480, width: "100%", borderRadius: 8 }}>
            <AgGridReact ref={gridRef} rowData={drivers} columnDefs={COL_DEFS}
              pagination paginationPageSize={12}
              defaultColDef={{ sortable: true, filter: true, resizable: true }} />
          </div>
        </div>
      )}

      {/* ══ TAB: Analysis ══ */}
      {tab === "compare" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>

          <div style={{ background: "#fff", borderRadius: 14, boxShadow: "0 2px 14px rgba(73,105,171,0.09)", padding: "18px 20px" }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#1a2340", marginBottom: 14 }}>Revenue by Product Line</div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={productData} margin={{ top: 5, right: 10, left: 10, bottom: 60 }} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f8" />
                <XAxis type="number" tickFormatter={(v) => `₹${(v/100000).toFixed(0)}L`} tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={100} />
                <Tooltip formatter={(v) => `₹${Number(v).toLocaleString("en-IN")}`} />
                <Bar dataKey="value" name="Revenue" radius={[0,4,4,0]}>
                  {productData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div style={{ background: "#fff", borderRadius: 14, boxShadow: "0 2px 14px rgba(73,105,171,0.09)", padding: "18px 20px" }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#1a2340", marginBottom: 14 }}>Revenue Mix by Channel</div>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={channelData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                  outerRadius={110} innerRadius={55} paddingAngle={3} label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`}
                  labelLine={{ stroke: "#ccc", strokeWidth: 1 }}>
                  {channelData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => `₹${Number(v).toLocaleString("en-IN")}`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
